/**
 * Choosing a language.
 *
 * The Electron kiosk had this on its welcome screen in three languages and the
 * port dropped it, which left `@mgc/i18n`'s English catalogue unreachable at
 * runtime — a translated application nobody could reach the translation of.
 *
 * Two sizes, because it belongs in two places. Large on the attract screen,
 * where it is one of only two things to press; small under the category rail,
 * because a customer who realises three taps in that they cannot read the menu
 * should not have to abandon their basket to change it.
 *
 * The switch iterates `LOCALES`, so a third language is one catalogue in
 * `@mgc/i18n` and no change here.
 */
import { LOCALES, LOCALE_NAMES } from '@mgc/i18n'
import type { Locale } from '@mgc/i18n'
import { useKiosk } from '@/store/kiosk'

export function LocaleSwitch({
  size = 'large',
  ground = 'brand',
  onChoose
}: {
  size?: 'large' | 'small'
  /**
   * Which surface it is standing on.
   *
   * The frosted-white pill exists because this control's first home is the
   * attract screen's brand yellow; on the white sheet the menu now opens it
   * in, that same pill is invisible and only the chosen chip has any shape.
   */
  ground?: 'brand' | 'paper'
  /**
   * Called after a language is picked, for a caller that is a sheet and wants
   * to close on the choice. It is a prop rather than a click that bubbles to
   * the caller because this component deliberately stops propagation — the
   * attract screen is one big button and a tap in here must not also start an
   * order.
   */
  onChoose?: () => void
}) {
  const locale = useKiosk((s) => s.locale)
  const setLocale = useKiosk((s) => s.setLocale)

  // One language is not a choice, and a control with a single option reads as
  // broken rather than as settled.
  if (LOCALES.length < 2) return null

  const large = size === 'large'

  return (
    <fieldset
      aria-label="Language"
      /*
        Large, it sits on the brand yellow, so the pill is a frosted white
        rather than a bordered one — a hairline border on yellow is a line
        nobody sees from where the customer is standing.
      */
      /*
        Small, it is the same pill at half the size. It used to stack, because
        it lived at the foot of a 10rem category rail and two endonyms side by
        side did not fit in one. The rail is gone — it now sits at the end of
        the category strip in the console, where the panel is 1080 wide and
        there is no reason for it to be a different shape from the large one.
      */
      className={`flex shrink-0 items-center gap-1.5 rounded-[var(--radius-pill)] ${
        large
          ? ground === 'brand'
            ? 'border-2 border-white/50 bg-white/35 p-2 backdrop-blur-md'
            : 'border-2 border-[var(--color-line)] bg-[var(--color-paper-sunk)] p-2'
          : 'border border-[var(--color-line)] bg-[var(--color-paper-raised)] p-1.5'
      }`}
      /*
        The attract screen is one big button; a press in here is a language
        choice and must not also start an order. Both events have to be
        stopped: the click for a tap, and the keydown because Enter on a chip
        bubbles as a keydown *before* it arrives as a click, and the screen
        behind treats Enter as «begin».
      */
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {LOCALES.map((option: Locale) => (
        <button
          key={option}
          type="button"
          aria-pressed={option === locale}
          onClick={() => {
            setLocale(option)
            onChoose?.()
          }}
          /*
            The chosen one is the *dark* chip, not the yellow one. This control
            appears on the brand yellow and on white, and a yellow chip is
            invisible on the first of those — "selected is dark, with the brand
            as its text" is the one pairing that reads on both. It is
            deliberately *not* the product sheet's idiom, which fills a chosen
            option with `--color-brand-soft`: that one only ever sits on paper,
            and this one cannot.
          */
          className={`tap display-upright rounded-[var(--radius-pill)] ${
            large ? 'px-10 text-[length:var(--text-kiosk-lg)]' : 'min-h-16 px-5 text-[length:var(--text-kiosk-sm)]'
          } ${
            option === locale
              ? 'bg-[var(--color-dark)] text-[var(--color-brand)] shadow-[var(--shadow-lift)]'
              : large && ground === 'brand'
                ? 'text-[var(--color-brand-ink)]/60'
                : 'text-[var(--color-ink-soft)]'
          }`}
        >
          {LOCALE_NAMES[option]}
        </button>
      ))}
    </fieldset>
  )
}
